<template>
  <div class="wrapper">
    <div class="card card-custom shadow-none">
      <div
        class="card-body d-flex align-items-center justify-content-between px-0"
      >
        <div class="input-icon">
          <input
            type="text"
            class="form-control rounded-pill"
            placeholder="Search..."
            :disabled="disabled"
            v-model="term"
            @input="search"
          />

          <span class="mt-1 mx-2">
            <i class="material-icons text-muted">search</i>
          </span>
        </div>

        <b-button
          variant="dark"
          class="btn-pill font-weight-bolder"
          :disabled="disabled"
          @click="create"
          v-if="$permissions.groups.create"
          >New Group</b-button
        >
      </div>
    </div>

    <div class="card card-custom shadow-none">
      <div class="card-body p-0">
        <div class="blockui shadow-sm zindex-1" v-if="querying">
          <span class="btn spinner spinner-lg spinner-dark spinner-left"
            >Loading...</span
          >
        </div>

        <div
          class="d-flex align-items-center justify-content-center"
          v-if="empty"
        >
          <span v-if="querying">...</span>
          <span v-else-if="error">{{ error }}</span>
          <span v-else>No results found.</span>
        </div>

        <div v-for="(object, i) in objects" :key="i">
          <div class="card card-custom bg-white border mb-4">
            <div class="card-body row p-4">
              <div class="col-10 d-flex align-items-center">
                <span class="font-size-lg font-weight-bolder">{{
                  object.name
                }}</span>
              </div>
              <div
                class="col-2 d-flex align-items-center justify-content-center justify-content-lg-end"
              >
                <button
                  class="btn btn-icon btn-circle btn-hover-dark mr-4"
                  title="Preview"
                  :disabled="disabled"
                  @click="view(object)"
                  v-b-tooltip.hover
                >
                  <i class="material-icons icon-lg">visibility</i>
                </button>

                <button
                  class="btn btn-icon btn-circle btn-hover-dark mr-4"
                  title="Edit"
                  :disabled="disabled"
                  @click.stop="update(object)"
                  v-b-tooltip.hover
                  v-if="$permissions.groups.update"
                >
                  <i class="material-icons icon-lg">edit</i>
                </button>

                <button
                  class="btn btn-icon btn-circle btn-hover-dark"
                  title="Delete"
                  :disabled="disabled"
                  @click.stop="confirm(object, object.name)"
                  v-b-tooltip.hover
                  v-if="$permissions.groups.delete"
                >
                  <i class="material-icons icon-lg">delete</i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <paginator :disabled="disabled" :size="page_size" :total="total" />

        <form-modal ref="form_modal" />
        <c-details ref="details-modal" />
      </div>
    </div>
  </div>
</template>

<script>
import Paginator from "@/components/paginator.vue";
import GroupsService from "@/services/groups.service";

import FormModal from "./components/form";
import CDetails from "./components/details";
import { formatErrorMessage } from "@/utils/random_utils";

export default {
  name: "Groups",
  components: {
    Paginator,
    FormModal,
    CDetails,
  },
  methods: {
    search() {
      clearTimeout(this.debounce);

      this.debounce = setTimeout(() => {
        let params = {};

        if (this.term) params.search = this.term;

        this.query(params);
      }, 500);
    },
    query(params = {}) {
      params["page"] = this.$route.query.page || 1;
      params["page_size"] = this.page_size;

      if (this.term) params.search = this.term;

      this.error = null;
      this.querying = true;

      GroupsService.query(params)
        .then(({ count, data }) => {
          this.total = count;
          this.objects = data.map((item) => {
            return item;
          });
        })
        .catch(({ message }) => {
          this.error = "There was an error loading groups.";
        })
        .finally(() => {
          this.querying = false;
        });
    },
    confirm(object, name) {
      let message = this.$createElement("span", [
        `Are you sure you want to delete "`,
        this.$createElement("strong", name),
        `"?`,
      ]);

      this.$bvModal
        .msgBoxConfirm(message, {
          title: "Confirm delete",
          buttonSize: "md",
          okTitle: "Delete",
          okVariant: "dark btn-pill font-weight-bolder",
          cancelTitle: "Cancel",
          cancelVariant: "secondary btn-pill font-weight-bolder",
          centered: true,
        })
        .then((confirmed) => {
          if (confirmed) this.remove(object);
        });
    },
    remove(object) {
      this.deleting = true;

      GroupsService.delete(object.id)
        .then((response) => {
          this.$bvModal
            .msgBoxOk(`Group deleted successfully.`, {
              title: "Success",
              buttonSize: "md",
              okTitle: "Ok",
              okVariant: "dark btn-pill font-weight-bolder",
              centered: true,
            })
            .then((value) => {
              this.query();
            });
        })
        .catch(({ message }) => {
          this.error = formatErrorMessage(message);
          this.$bvModal.msgBoxOk(this.error, {
            title: "Error",
            buttonSize: "md",
            okTitle: "Ok",
            okVariant: "dark btn-pill font-weight-bolder",
            centered: true,
          });
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    view(object) {
      this.$refs["details-modal"].open(object.id);
    },
    create() {
      this.$refs.form_modal.show();
    },
    update(object) {
      this.$refs.form_modal.show(object.id);
    },
  },
  watch: {
    "$route.query.page"(value) {
      this.query();
    },
  },
  computed: {
    empty() {
      return this.objects.length === 0;
    },
    disabled() {
      return this.querying || this.deleting;
    },
  },
  data() {
    return {
      debounce: null,
      term: null,
      error: null,
      querying: false,
      deleting: false,
      total: 0,
      page_size: 20,
      objects: [],
    };
  },
  mounted() {
    if (!this.$permissions.groups.read) this.$router.push({ name: "403" });
    this.query();
  },
};
</script>
